import type { I18nMessageObject } from '@_/types';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { getI18nValue } from '@_/utils';
import { Image } from '../Image/index.ts';

export interface AvatarProps {
  className?: string;
  kind?: 'organization' | 'person';
  name?: string | I18nMessageObject;
  rounded?: boolean;
  src?: string | null;
}

export function Avatar({ className, kind = 'person', name, rounded = true, src }: AvatarProps) {
  const { locale } = useIntl();

  const nameStr = typeof name !== 'string' ? getI18nValue(name, locale, true) : name;

  const initials = nameStr
    ? nameStr
        .trim()
        .split(/\b\s+/)
        .slice(0, kind === 'person' ? 2 : 1)
        .map(word => word[0])
        .join('')
    : undefined;

  return (
    <span
      className={clsx(
        className,
        'grid aspect-square shrink-0 align-middle *:col-start-1 *:row-start-1',
        rounded ? 'rounded-full *:rounded-full' : 'rounded-[25%] *:rounded-[25%]',
        !src && 'outline -outline-offset-1 outline-black/5'
      )}
    >
      {src ? (
        <Image className="aspect-square size-full" data-slot="avatar" fit="cover" src={src} />
      ) : (
        <>
          <svg
            className={clsx(
              'size-full bg-gray-800 fill-current p-[5%] text-[48px] font-medium text-white uppercase select-none'
            )}
            data-slot="avatar"
            viewBox="0 0 100 100"
          >
            <text
              x="50%"
              y="50%"
              alignmentBaseline="middle"
              dominantBaseline="middle"
              textAnchor="middle"
              dy=".125em"
            >
              {initials}
            </text>
          </svg>
        </>
      )}
    </span>
  );
}
